import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Menu, X, Heart, LogOut, LayoutDashboard, MessageCircle, User, ShoppingBag, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  // Close mobile menu on route change
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  // Lock body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    logout();
    toast.success('Logged out');
    navigate('/');
  };

  const isActive = (path) => {
    if (path === '/catalog') return location.pathname === '/catalog';
    if (path === '/brands') return location.pathname === '/brands';
    return location.pathname === path;
  };

  const navLink = (path, label, testId) => (
    <Link
      to={path}
      className={`text-xs font-semibold tracking-[0.15em] uppercase transition-colors py-1 border-b-2 ${
        isActive(path) ? 'text-foreground border-primary' : 'text-muted-foreground hover:text-foreground border-transparent'
      }`}
      data-testid={testId}
    >
      {label}
    </Link>
  );

  const mobileLink = (path, label, icon) => {
    const Icon = icon;
    const active = isActive(path);
    return (
      <Link
        to={path}
        className={`flex items-center justify-between py-3.5 px-1 border-b border-border/50 transition-colors ${active ? 'text-primary' : 'text-foreground'}`}
        onClick={() => setMobileMenuOpen(false)}
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
          <span className="text-sm font-medium">{label}</span>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </Link>
    );
  };

  return (
    <>
      <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-[1280px]">
          <div className="flex h-16 items-center justify-between gap-8">
            <Link to="/" className="flex-shrink-0" data-testid="header-logo-link">
              <img src="/vwatch-monogram.png" alt="V watch" className="h-[35px] w-auto md:h-[39px] block" />
            </Link>

            <nav className="hidden md:flex items-center gap-8">
              {navLink('/catalog', 'Catalog', 'header-catalog-link')}
              {navLink('/brands', 'Brands', 'header-brands-link')}
              {navLink('/about', 'About', 'header-about-link')}
            </nav>

            <div className="flex items-center gap-4">
              {user && (
                <div className="hidden md:flex items-center gap-4">
                  <Link to="/wishlist" className="text-muted-foreground hover:text-foreground transition-colors"><Heart className="h-4 w-4" /></Link>
                  <Link to="/messages" className="text-muted-foreground hover:text-foreground transition-colors"><MessageCircle className="h-4 w-4" /></Link>
                </div>
              )}

              {user ? (
                <div className="hidden md:flex items-center gap-3">
                  <button onClick={() => navigate('/profile')} className="text-xs text-muted-foreground hover:text-foreground transition-colors">{user.name}</button>
                  {(user.role === 'SELLER' || user.role === 'ADMIN') && (
                    <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => navigate(user.role === 'ADMIN' ? '/admin/dashboard' : '/seller/dashboard')}>
                      <LayoutDashboard className="h-3.5 w-3.5 mr-1" />
                      {user.role === 'ADMIN' ? 'Admin' : 'Dashboard'}
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleLogout}><LogOut className="h-3.5 w-3.5" /></Button>
                </div>
              ) : (
                <div className="hidden md:flex items-center gap-2">
                  <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => navigate('/login')}>Sign In</Button>
                  <Button size="sm" className="h-8 text-xs" onClick={() => navigate('/register')}>Sign Up</Button>
                </div>
              )}

              <button
                className="md:hidden p-2 relative z-[60]"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="header-mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Full-screen mobile menu overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden" data-testid="header-mobile-menu">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-background/98 backdrop-blur-md" onClick={() => setMobileMenuOpen(false)} />

          {/* Menu content */}
          <div className="relative z-10 flex flex-col h-full pt-20 px-6 pb-8 overflow-y-auto">
            {/* Main nav */}
            <div className="mb-6">
              <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-muted-foreground mb-2 px-1">Navigate</p>
              {mobileLink('/catalog', 'Catalog')}
              {mobileLink('/brands', 'Brands')}
              {mobileLink('/about', 'About')}
            </div>

            {/* User section */}
            {user && (
              <div className="mb-6">
                <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-muted-foreground mb-2 px-1">Account</p>
                {mobileLink('/profile', 'Profile', User)}
                {mobileLink('/orders', 'My Orders', ShoppingBag)}
                {mobileLink('/wishlist', 'Wishlist', Heart)}
                {mobileLink('/messages', 'Messages', MessageCircle)}
                {(user.role === 'SELLER' || user.role === 'ADMIN') && mobileLink(
                  user.role === 'ADMIN' ? '/admin/dashboard' : '/seller/dashboard',
                  user.role === 'ADMIN' ? 'Admin Dashboard' : 'Seller Dashboard',
                  LayoutDashboard
                )}
              </div>
            )}

            {/* Auth buttons */}
            <div className="mt-auto pt-6 border-t border-border/50">
              {user ? (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                  <Button variant="outline" size="sm" className="text-xs text-red-400 border-red-800/30 hover:bg-red-900/20" onClick={() => { handleLogout(); setMobileMenuOpen(false); }}>
                    <LogOut className="h-3.5 w-3.5 mr-1" /> Sign Out
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <Button className="w-full h-11" onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}>Sign In</Button>
                  <Button variant="outline" className="w-full h-11" onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}>Create Account</Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
